import React, { memo } from "react";
import {
  TouchableOpacity,
  ActivityIndicator,
  View,
  type TouchableOpacityProps,
} from "react-native";
import { AppText, type FontWeight } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "secondary-destructive"
  | "destructive"
  | "success"
  | "blue"
  | "ghost"
  | "text"
  | "tertiary"
  | "tertiary-destructive"
  | "outline"
  | "outline-warning"
  | "solid";

export interface ButtonProps extends TouchableOpacityProps {
  /** Button label text. Omit together with `iconOnly` for icon-only mode. */
  label?: TranslationKeys;
  onPress: () => void;
  variant?: ButtonVariant;
  loading?: boolean;
  disabled?: boolean;
  /** Icon element rendered to the left of the label. */
  icon?: React.ReactNode;
  /** Icon element rendered to the right of the label. */
  trailingIcon?: React.ReactNode;
  /** Renders a compact square button with only the icon (no label). */
  iconOnly?: boolean;
  /** Forwarded to the underlying TouchableOpacity for testing. */
  testID?: string;
  /** Accessible label announced by screen readers. Defaults to `label`. */
  accessibilityLabel?: TranslationKeys;
  /**
   * Optional prefix text rendered before `label` in a muted color.
   * Only used by the `"tertiary"` variant (two-tone button from Figma 12027-38711).
   */
  prefixLabel?: TranslationKeys;
  className?: string;
  labelClassName?: string;
}

/**
 * ActivityIndicator color per variant.
 * ActivityIndicator takes a JS string — not CSS — so we map design token
 * hex values here rather than in Tailwind classNames.
 * ghost/secondary/text → dark-text-20 (#6C717A), others → white (#FFFFFF).
 */
const SPINNER_COLORS: Record<ButtonVariant, string> = {
  primary: "#FFFFFF",
  secondary: "#FF1654", // primary / Main Color/50
  ghost: "#6C717A", // dark-text-20
  destructive: "#FFFFFF",
  success: "#FFFFFF",
  blue: "#FFFFFF",
  text: "#FF1654", // primary / Main Color/50
  tertiary: "#FF1654", // primary / Main Color/50
  outline: "#FF1654", // primary / Main Color/50
  "secondary-destructive": "#EA333C", // Red / Red 50
  "tertiary-destructive": "#EA333C", // Red / Red 50
  "outline-warning": "#FBA247", // Orange / Orange 50
  solid: "#FFFFFF",
};

const VARIANT_STYLES: Record<
  ButtonVariant,
  { container: string; color: string; weight: FontWeight }
> = {
  // Figma: Primary — solid red fill, white label
  primary: {
    container: "bg-primary border border-primary",
    color: "text-white",
    weight: "semibold",
  },
  // Figma: Outline (active) — stroke border, red label on white
  secondary: {
    container: "bg-white border border-stroke-50",
    color: "text-primary",
    weight: "semibold",
  },
  // Figma: Outline (active) — red border, red label on white
  "secondary-destructive": {
    container: "bg-white border border-error",
    color: "text-error",
    weight: "semibold",
  },
  // Figma: Outline (active) — red border, red label on white
  outline: {
    container: "bg-white border border-primary",
    color: "text-primary",
    weight: "semibold",
  },
  // Figma: Outline Warning — orange border, orange label on white
  "outline-warning": {
    container: "bg-white border border-orange-50",
    color: "text-orange-50",
    weight: "semibold",
  },
  // Custom: Success — green fill, white label
  success: {
    container: "bg-green-50 border border-green-50",
    color: "text-white",
    weight: "semibold",
  },
  // Custom: Blue — blue fill, white label
  blue: {
    container: "bg-blue-50 border border-blue-50",
    color: "text-white",
    weight: "semibold",
  },
  // Figma: Ghost (inactive) — subtle gray fill, no border, muted label
  ghost: {
    container: "bg-stroke-20 border border-stroke-20",
    color: "text-dark-text-20",
    weight: "medium",
  },
  // Custom: destructive — red error fill, white label
  destructive: {
    container: "bg-error border border-error",
    color: "text-white",
    weight: "semibold",
  },
  // Figma: Text (active) — no fill, no border, red label
  text: {
    container: "bg-transparent border border-transparent",
    color: "text-primary",
    weight: "semibold",
  },
  // Figma 12027-38711: gray fill, two-tone text (gray prefix + pink action)
  tertiary: {
    container: "bg-stroke-30 border border-stroke-30",
    color: "text-primary",
    weight: "semibold",
  },
  // Custom: tertiary-destructive — gray fill, two-tone text with red action
  "tertiary-destructive": {
    container: "bg-stroke-30 border border-stroke-30",
    color: "text-error",
    weight: "semibold",
  },
  // Custom: solid — supporting-text fill, white label
  solid: {
    container: "bg-supporting-text border border-supporting-text",
    color: "text-white",
    weight: "semibold",
  },
};

export const Button = memo(function Button({
  label,
  onPress,
  variant = "primary",
  loading = false,
  disabled = false,
  icon,
  trailingIcon,
  iconOnly = false,
  testID,
  accessibilityLabel,
  prefixLabel,
  className,
  labelClassName,
  ...rest
}: ButtonProps) {
  const { t } = useI18n();
  const styles = VARIANT_STYLES[variant];
  const isDisabled = disabled || loading;
  const a11yLabel = (accessibilityLabel ?? label) ? t((accessibilityLabel ?? label)!) : undefined;

  const sizeClasses = iconOnly ? "w-11 h-11 p-0" : `px-4 lg:px-6 py-3`;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isDisabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      className={cn(
        "flex-row items-center justify-center rounded-md",
        sizeClasses,
        styles.container,
        isDisabled ? "opacity-50" : "",
        className
      )}
      {...rest}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={SPINNER_COLORS[variant]}
          testID="button-loading-indicator"
        />
      ) : (
        <>
          {!!icon && !iconOnly && <View className="me-2">{icon}</View>}
          {iconOnly ? (
            icon
          ) : (
            <>
              {variant === "tertiary" && !!prefixLabel && (
                <AppText
                  weight="medium"
                  className="text-base tracking-[0.16px] text-supporting-text me-1"
                >
                  {t(prefixLabel)}
                </AppText>
              )}
              <AppText
                weight={styles.weight}
                className={cn("text-base tracking-[0.16px]", styles.color, labelClassName)}
              >
                {label ? t(label) : null}
              </AppText>
            </>
          )}
          {!!trailingIcon && !iconOnly && <View className="ms-2">{trailingIcon}</View>}
        </>
      )}
    </TouchableOpacity>
  );
});
